import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import EmailVerificationPage from './EmailVerificationPage';
import AdminDashboard from './AdminDashboard';
import GeneratedImagesPage from './GeneratedImagesPage';
import GeneratedVideosPage from './GeneratedVideosPage';
import SharedChatView from './SharedChatView';
import ThemeToggle from './ThemeToggle';
import ThemeTransition from './ThemeTransition';
import ChatHistoryPage from './ChatHistoryPage';

import {
  MessageSquare,
  Image,
  Download as DownloadIcon,
  Share2,
  Copy as CopyIcon,
  Video,
  Sparkles,
  Brain,
  Home,
  User,
  LogOut,
  UserPlus, // Add this
  LogIn,    // Add this if not already imported
  History,
  Settings,
  CreditCard,
  Moon,
  Sun,
  Send,
  Copy,
  Check,
  X,
  Menu,
  ChevronRight,
  Crown,
  FileText,
  Search,
  Trash2,
  Edit,
  Plus,
  Bot,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Zap,
  Infinity,
  Shield,
  Cpu,
  Rocket,
  Code,
  Globe,
  Mail, 
  Palette,
  Film,
  BarChart3,
  Lock,
  Unlock,
  Upload,
  Download,
  ExternalLink,
  Star,
  TrendingUp,
  Users,
  Gift,
  Clock,
  Calendar,
  Award,
  Heart,
  Smile,
  ThumbsUp,
  Lightbulb,
  Compass,
  Layers,
  PenTool,
  Music,
  Camera,
  Mic,
  Terminal,
  Filter,
  Eye,
  EyeOff,
  Bell,
  Inbox,
  Flag,
  HelpCircle,
  Info,
  AlertTriangle,
  Coffee
} from 'lucide-react';

// API base URL
export const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:4040/api'
  : '/api';


// Add this at the top of App.js, right after imports
const themes = {
  dark: {
    background: 'bg-gray-900',
    text: 'text-white',
    secondaryBg: 'bg-gray-800',
    secondaryText: 'text-gray-400',
    border: 'border-gray-800',
    navbar: 'bg-gray-900 border-gray-800',
    input: 'bg-gray-800 border-gray-700',
    button: 'bg-purple-600 hover:bg-purple-700',
    card: 'bg-gray-800/50 border-gray-700',
    hover: 'hover:bg-gray-700',
  },
  light: {
    background: 'bg-gray-50',
    text: 'text-gray-900',
    secondaryBg: 'bg-white',
    secondaryText: 'text-gray-600',
    border: 'border-gray-200',
    navbar: 'bg-white border-gray-200',
    input: 'bg-white border-gray-300',
    button: 'bg-purple-600 hover:bg-purple-700',
    card: 'bg-white border-gray-200',
    hover: 'hover:bg-gray-100',
  }
};

// Theme Context with proper types
export const ThemeContext = React.createContext({
  theme: 'dark',
  toggleTheme: () => {},
  themeStyles: themes.dark
});



// Generate anonymous ID for tracking
const generateAnonymousId = () => {
  const savedId = localStorage.getItem('anonymousId');
  if (savedId) return savedId;

  const newId = 'anon_' + Math.random().toString(36).substr(2, 9);
  localStorage.setItem('anonymousId', newId);
  return newId;
};

// Modern Navbar Component
function Navbar({ isAuthenticated, user, logout }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme, themeStyles } = React.useContext(ThemeContext); // Get theme from context
  // Hide navbar on success page
  if (location.pathname === '/success') {
    return null;
  }

  const isPaidUser = isAuthenticated && user?.plan === 'paid';

  return (
    <nav className={`sticky top-0 z-50 ${themeStyles.navbar} border-b ${themeStyles.border}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo - stays the same regardless of theme */}
          <Link to={isPaidUser ? "/chat" : "/"} className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
              <img 
                src="https://app.gospelflixstream.com/aigenesis.jpg" 
                alt="AI Genesis Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className={`text-lg font-semibold ${themeStyles.text}`}>AI Genesis</h1>
              <div className="flex items-center space-x-1">
                <span className="text-xs text-purple-400 font-bold">T.O.P.</span>
                <span className={`text-xs ${themeStyles.secondaryText} hidden sm:inline`}>
                  Transcendent Open Protocol
                </span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {isPaidUser ? (
              <>
                <NavLink to="/chat" themeStyles={themeStyles}>Chat</NavLink>
                <NavLink to="/generate" themeStyles={themeStyles}>Studio</NavLink>
                {/* Add Generated Content dropdown */}
                <div className="relative group">
                  <button className={`flex items-center space-x-1 px-3 py-2 text-sm font-medium ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}>
                    <span>My Content</span>
                    <ChevronRight className="w-4 h-4 transform group-hover:rotate-90 transition-transform" />
                  </button>
                  <div className={`absolute left-0 mt-2 w-48 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50`}>
                    <Link to="/images" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} rounded-t-lg ${themeStyles.text}`}>
                      <Image className="w-4 h-4 text-purple-400" />
                      <span>My Images</span>
                    </Link>
                    <Link to="/videos" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} rounded-b-lg ${themeStyles.text}`}>
                      <Film className="w-4 h-4 text-blue-400" />
                      <span>My Videos</span>
                    </Link>
                  </div>
                </div>
                <NavLink to="/profile" themeStyles={themeStyles}>Profile</NavLink>
              </>
            ) : (
              <>
                <NavLink to="/" themeStyles={themeStyles}>Home</NavLink>
                <NavLink to="/chat" themeStyles={themeStyles}>Chat</NavLink>
                <NavLink to="/generate" themeStyles={themeStyles}>Studio</NavLink>
                <NavLink to="/pricing" themeStyles={themeStyles}>Pricing</NavLink>
              </>
            )}
            <NavLink to="/contact" themeStyles={themeStyles}>Contact</NavLink>
            <NavLink to="/privacy" themeStyles={themeStyles}>Privacy</NavLink>

            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="relative group">
                  <button className={`flex items-center space-x-2 px-3 py-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg transition-colors ${themeStyles.text}`}>
                    <User className="w-4 h-4" />
                    <span className="text-sm">{user?.email?.split('@')[0]}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <div className={`absolute right-0 mt-2 w-48 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50`}>
                    <Link to="/profile" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} rounded-t-lg ${themeStyles.text}`}>
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    <Link to="/history" className={`flex items-center space-x-2 p-3 ${themeStyles.hover} ${themeStyles.text}`}>
                      <History className="w-4 h-4" />
                      <span>History</span>
                    </Link>
                    
                    <button
                      onClick={logout}
                      className="flex items-center space-x-2 p-3 hover:bg-red-500/20 text-red-400 w-full rounded-b-lg"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
                {/* FIXED: Theme toggle button now works */}
                <button
                  onClick={toggleTheme}
                  className={`p-2 ${themeStyles.secondaryBg} ${themeStyles.hover} rounded-lg transition-colors ${themeStyles.text}`}
                  aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors text-white">
                  Sign In
                </Link>
                <Link to="/signup" className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors text-white">
                  Sign Up
                </Link>
                {/* FIXED: Theme toggle button now works for non-authenticated users too */}
                 <ThemeToggle 
              theme={theme} 
              toggleTheme={toggleTheme} 
              themeStyles={themeStyles} 
            />
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 ${themeStyles.secondaryBg} rounded-lg ${themeStyles.text}`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden ${theme === 'dark' ? 'bg-gray-800/95' : 'bg-white/95'} backdrop-blur-lg rounded-lg border ${themeStyles.border} overflow-hidden`}
            >
              <div className="p-4 space-y-2">
                {/* Mobile navigation links - same structure but with theme classes */}
                {isPaidUser ? (
                  <>
                    <MobileNavLink to="/chat" icon={<MessageSquare />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Chat
                    </MobileNavLink>
                    <MobileNavLink to="/generate" icon={<Palette />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Studio
                    </MobileNavLink>
                    
                    {/* Gallery Section for Paid Users */}
                    <div className="relative">
                      <div className={`flex items-center justify-between px-3 py-2 text-xs font-medium ${themeStyles.secondaryText} uppercase tracking-wider`}>
                        <span>My Gallery</span>
                      </div>
                      <div className="ml-4 space-y-1">
                        <MobileNavLink to="/images" icon={<Image className="w-4 h-4 text-purple-400" />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                          My Images
                        </MobileNavLink>
                        <MobileNavLink to="/videos" icon={<Film className="w-4 h-4 text-blue-400" />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                          My Videos
                        </MobileNavLink>
                      </div>
                    </div>
                    
                    <MobileNavLink to="/profile" icon={<User />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Profile
                    </MobileNavLink>
                  </>
                ) : (
                  <>
                    <MobileNavLink to="/" icon={<Home />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Home
                    </MobileNavLink>
                    <MobileNavLink to="/chat" icon={<MessageSquare />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Chat
                    </MobileNavLink>
                    <MobileNavLink to="/generate" icon={<Palette />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Studio
                    </MobileNavLink>
                    <MobileNavLink to="/pricing" icon={<CreditCard />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Pricing
                    </MobileNavLink>
                  </>
                )}
                
                <MobileNavLink to="/contact" icon={<Mail />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                  Contact
                </MobileNavLink>
                <MobileNavLink to="/privacy" icon={<Shield />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                  Privacy
                </MobileNavLink>

                {/* Add theme toggle to mobile menu */}
               <ThemeToggle 
              theme={theme} 
              toggleTheme={toggleTheme} 
              themeStyles={themeStyles} 
            />

                {isAuthenticated ? (
                  <>
                    {/* Gallery Section for Free Users who are logged in */}
                    {!isPaidUser && (
                      <div className="relative">
                        <div className={`flex items-center justify-between px-3 py-2 text-xs font-medium ${themeStyles.secondaryText} uppercase tracking-wider`}>
                          <span>My Gallery</span>
                        </div>
                        <div className="ml-4 space-y-1">
                          <MobileNavLink to="/images" icon={<Image className="w-4 h-4 text-purple-400" />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                            My Images
                          </MobileNavLink>
                          <MobileNavLink to="/videos" icon={<Film className="w-4 h-4 text-blue-400" />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                            My Videos
                          </MobileNavLink>
                        </div>
                      </div>
                    )}
                    
                    <MobileNavLink to="/profile" icon={<User />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Profile
                    </MobileNavLink>
                    <MobileNavLink to="/history" icon={<History />} onClick={() => setIsMenuOpen(false)} themeStyles={themeStyles}>
                      Chat History
                    </MobileNavLink>
                    <button
                      onClick={() => {
                        logout();
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center space-x-3 w-full p-3 text-red-400 hover:bg-red-500/20 rounded-lg"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link 
                      to="/login" 
                      className={`flex items-center justify-center space-x-2 w-full p-3 ${theme === 'dark' ? 'bg-gray-700/50 hover:bg-gray-700' : 'bg-gray-200 hover:bg-gray-300'} rounded-lg text-sm font-medium ${themeStyles.text}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <LogIn className="w-4 h-4" />
                      <span>Sign In</span>
                    </Link>
                    <Link 
                      to="/signup" 
                      className="flex items-center justify-center space-x-2 w-full p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Sign Up</span>
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
}

// Updated NavLink component with theme support
function NavLink({ to, children, themeStyles }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const { theme } = React.useContext(ThemeContext); // Add this line

  return (
    <Link
      to={to}
      className={`px-3 py-2 text-sm font-medium transition-colors ${
        isActive
          ? themeStyles.text
          : themeStyles.secondaryText
      } hover:${themeStyles.text}`}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ to, icon, children, onClick, themeStyles }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  const { theme } = React.useContext(ThemeContext); // Add this line to get theme

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
        isActive
          ? `${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'} ${themeStyles.text}`
          : `${themeStyles.secondaryText} hover:${themeStyles.secondaryBg} hover:${themeStyles.text}`
      }`}
    >
      <div className={themeStyles.secondaryText}>{icon}</div>
      <span className="text-sm">{children}</span>
    </Link>
  );
}

// Sidebar Component
function Sidebar({ isOpen, onClose, chats, activeChat, onNewChat, onSelectChat, onDeleteChat }) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredChats = chats.filter(chat =>
    chat.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 md:hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        exit={{ x: -300 }}
        className="absolute left-0 top-0 h-full w-80 bg-gray-900 border-r border-gray-800"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onNewChat}
              className="flex items-center space-x-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>New chat</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-purple-500"
            />
          </div>

          <div className="space-y-1">
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                  activeChat === chat.id
                    ? 'bg-gray-800'
                    : 'hover:bg-gray-800'
                }`}
                onClick={() => onSelectChat(chat.id)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">
                    {chat.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(chat.created_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteChat(chat.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 text-red-400 rounded transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function ChatPage({ isAuthenticated, user, anonymousUsage, setAnonymousUsage }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [chatTitle, setChatTitle] = useState('New Chat');
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [showLimitBannerb, setShowLimitBannerb] = useState(false); // Add this line
  const [showLimitBanner, setShowLimitBanner] = useState(false); // Add this line
  const messagesEndRef = useRef(null);

  // Debug logging
  useEffect(() => {
    console.log('Active chat:', activeChat);
    console.log('Messages:', messages);
    console.log('Chats:', chats);
  }, [activeChat, messages, chats]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchChats();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingResponse]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      console.log('Fetching chats...');
      const response = await axios.get(`${API_URL}/chat/history`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Fetched chats response:', response.data);
      setChats(response.data.chats || []);
    } catch (error) {
      console.error('Failed to fetch chats:', error.response?.data || error.message);
    }
  };
  
  const generateChatTitle = async (chatId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/chat/generate-title/${chatId}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    if (response.data.success) {
      setChatTitle(response.data.title);
      fetchChats(); // Refresh the chat list
      return response.data.title;
    }
  } catch (error) {
    console.error('Failed to generate title:', error);
  }
  return null;
};

  const createNewChat = () => {
    console.log('Creating new chat');
    setMessages([]);
    setActiveChat(null);
    setChatTitle('New Chat');
    setSidebarOpen(false);
    setStreamingResponse('');
  };

  const selectChat = async (chatId) => {
    console.log('Selecting chat:', chatId);
    setIsLoadingChat(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please login to load chats');
        return;
      }
      
      const response = await axios.get(`${API_URL}/chat/history/${chatId}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Chat loaded:', response.data);
      
      if (response.data.chat) {
        const chat = response.data.chat;
        
        // Set messages (already parsed by server)
        setMessages(chat.messages || []);
        setActiveChat(chat.id);
        setChatTitle(chat.title || 'New Chat');
        setSidebarOpen(false);
        setStreamingResponse('');
        
        // Clear any loading states
        setIsStreaming(false);
        setIsLoading(false);
        
        console.log('Chat loaded successfully:', chat.id, 'with', (chat.messages || []).length, 'messages');
      }
    } catch (error) {
      console.error('Failed to load chat:', error.response?.data || error.message);
      alert('Failed to load chat. Please try again.');
    } finally {
      setIsLoadingChat(false);
    }
  };

  const deleteChat = async (chatId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this chat?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/chat/history/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (activeChat === chatId) {
          createNewChat();
        }
        fetchChats();
      } catch (error) {
        console.error('Failed to delete chat:', error);
      }
    }
  };

  const updateChatTitle = async (chatId, newTitle) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/chat/history/${chatId}`, 
        { title: newTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchChats();
    } catch (error) {
      console.error('Failed to update chat title:', error);
    }
  };
  
  const downloadChat = async () => {
  if (!activeChat) {
    alert('No chat selected to download');
    return;
  }

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please login to download chats');
      return;
    }

    // Use full URL with port - MAKE SURE THIS MATCHES YOUR BACKEND PORT
    const baseUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:4040'  // Your backend port
      : ''; // In production, use relative URL or full domain
    
    const downloadUrl = `${baseUrl}${API_URL}/chat/download/${activeChat}?format=txt`;
    
    console.log('Downloading from:', downloadUrl); // Debug log
    
    const response = await fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Download failed:', errorText);
      
      if (response.status === 401) {
        alert('Authentication failed. Please login again.');
        return;
      }
      throw new Error(`Download failed: ${response.status}`);
    }

    // Check if response is HTML (phpMyAdmin error)
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('text/html')) {
      console.error('Received HTML instead of file download');
      alert('Download failed: Server returned HTML. This might be a routing issue.');
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${chatTitle?.replace(/[^a-z0-9]/gi, '_') || 'conversation'}-${new Date().getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
    
  } catch (error) {
    console.error('Failed to download chat:', error);
    alert('Failed to download chat. Please try again.');
  }
};

const shareChat = async () => {
  if (!activeChat) {
    alert('No chat selected to share');
    return;
  }

  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/chat/share/${activeChat}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ expiresIn: 7 })
    });

    const data = await response.json();
    if (data.success) {
      // Copy to clipboard
      navigator.clipboard.writeText(data.shareUrl);
      alert('Share link copied to clipboard! (expires in 7 days)');
    } else {
      alert('Failed to create share link');
    }
  } catch (error) {
    console.error('Failed to share chat:', error);
    alert('Failed to share chat. Please try again.');
  }
};

// Update the saveChatHistory function:
const saveChatHistory = async (chatMessages) => {
  if (!isAuthenticated || chatMessages.length === 0) return;

  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_URL}/chat/history`, {
      messages: chatMessages,
      chatId: activeChat
    }, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('Save chat response:', response.data);
    
    if (response.data.chat) {
      if (!activeChat) {
        setActiveChat(response.data.chat.id);
        setChatTitle(response.data.chat.title || 'New Chat');
      } else {
        // Update local title if changed
        if (response.data.chat.title !== chatTitle) {
          setChatTitle(response.data.chat.title);
        }
      }
      fetchChats();
      
      // Check if we should trigger title generation
      if (chatMessages.length >= 3) {
        // For new chats, wait a bit then check if title is still generic
        setTimeout(() => {
          if (chatTitle === 'New Chat' || chatTitle.includes('...') || chatTitle.length < 10) {
            generateChatTitle(response.data.chat.id || activeChat);
          }
        }, 1000);
      }
    }
  } catch (error) {
    console.error('Failed to save chat history:', error.response?.data || error.message);
  }
};


const sendMessage = async () => {
  if (!message.trim() || isLoading || isStreaming) return;

  // Check free limits for anonymous users
  if (!isAuthenticated) {
    const currentUsage = JSON.parse(localStorage.getItem('anonymousUsage') || '{"chat":0,"image":0,"video":0}');
    if (currentUsage.chat >= 5) {
      // Show limit banner for anonymous users
      setShowLimitBanner(true);
      return;
    }
  }

  // Check for authenticated users on free plan - FIXED
  if (isAuthenticated && user?.plan !== 'paid') {
    try {
      // First check server-side limits
      const response = await axios.get(`${API_URL}/usage/limits`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const { limits } = response.data;
      if (limits.chat.used >= limits.chat.max) {
        setShowLimitBannerb(true);
        return;
      }
    } catch (error) {
      console.error('Failed to check usage limits:', error);
      // If we can't check limits, proceed but server will handle it
    }
  }

    setIsLoading(true);
    setIsStreaming(true);
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    const currentMessage = message;
    setMessage('');
    setStreamingResponse('');

    try {
      const headers = {};
      if (!isAuthenticated) {
        headers['x-anonymous-id'] = generateAnonymousId();
      }

      const response = await fetch(`${API_URL}/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
          ...(isAuthenticated ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {})
        },
        body: JSON.stringify({
          message: currentMessage,
          history: messages,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') {
              setIsStreaming(false);
              setIsLoading(false);

              const aiMessage = {
                role: 'assistant',
                content: fullResponse,
                timestamp: new Date().toISOString()
              };
              const updatedMessages = [...newMessages, aiMessage];
              setMessages(updatedMessages);
              setStreamingResponse('');

              // Save/update chat history for authenticated users
             if (isAuthenticated) {
  saveChatHistory(updatedMessages);
  
  // Auto-generate title after 3 messages
  if (updatedMessages.length >= 3) {
    // Check if title is still generic
    const currentTitle = chatTitle || '';
    if (currentTitle === 'New Chat' || 
        currentTitle.includes('...') || 
        currentTitle.length < 15) {
      // Small delay to ensure chat is saved first
      setTimeout(() => {
        generateChatTitle(activeChat);
      }, 1500);
    }
  }
}

              // Update anonymous usage
              if (!isAuthenticated) {
                const currentUsage = JSON.parse(localStorage.getItem('anonymousUsage') || '{"chat":0,"image":0,"video":0}');
                currentUsage.chat = (currentUsage.chat || 0) + 1;
                localStorage.setItem('anonymousUsage', JSON.stringify(currentUsage));
                setAnonymousUsage(currentUsage);
              }

              return;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.content) {
                fullResponse += parsed.content;
                setStreamingResponse(fullResponse);
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setIsStreaming(false);
      setIsLoading(false);
      alert('Chat failed. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyMessage = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Loading Overlay */}
      {isLoadingChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Loading chat...</span>
          </div>
        </div>
      )}
      
       {/* Limit Banner */}
      {showLimitBanner && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <div>
                  <p className="font-medium text-sm">Free chat limit reached!</p>
                  <p className="text-gray-300 text-xs">Sign up to continue chatting</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/signup"
                  onClick={() => setShowLimitBanner(false)}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs"
                >
                  Sign Up
                </Link>
                <button
                  onClick={() => setShowLimitBanner(false)}
                  className="p-1 hover:bg-gray-700/50 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
       {/* Limit Bannerb */}
      {showLimitBannerb && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <div>
                  <p className="font-medium text-sm">Free chat limit reached!</p>
                  <p className="text-gray-300 text-xs">Upgrade your account for only 14.99 USD lifetime plan</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/pricing"
                  onClick={() => setShowLimitBanner(false)}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs"
                >
                  Upgrade
                </Link>
                <button
                  onClick={() => setShowLimitBanner(false)}
                  className="p-1 hover:bg-gray-700/50 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden md:flex flex-col w-64 border-r border-gray-800 bg-gray-900">
        <div className="p-4">
          <button
            onClick={createNewChat}
            className="flex items-center space-x-2 w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New chat</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
              Recent Chats
            </div>
            <div className="space-y-1">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  className={`group flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                    activeChat === chat.id
                      ? 'bg-gray-800 border-l-4 border-purple-500'
                      : 'hover:bg-gray-800'
                  }`}
                  onClick={() => selectChat(chat.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate font-medium">
                      {chat.title || 'Untitled Chat'}
                    </p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500">
                        {chat.message_count || 0} messages
                      </p>
                      <p className="text-xs text-gray-600">
                        {new Date(chat.updated_at || chat.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newTitle = prompt('Edit chat title:', chat.title);
                        if (newTitle && newTitle !== chat.title) {
                          updateChatTitle(chat.id, newTitle);
                        }
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-700 rounded transition-all"
                      title="Edit title"
                    >
                      <Edit className="w-3 h-3" />
                    </button>
                    <button
                      onClick={(e) => deleteChat(chat.id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 text-red-400 rounded transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
<div className="px-4 py-2 mb-4">
  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
    My Gallery
  </div>
  <div className="space-y-1">
    <Link
      to="/images"
      className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
    >
      <Image className="w-4 h-4 text-purple-400" />
      <span>My Images</span>
      <span className="ml-auto text-xs bg-gray-800 px-2 py-1 rounded-full">
        {/* You can fetch and display count here */}
      </span>
    </Link>
    <Link
      to="/videos"
      className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
    >
      <Film className="w-4 h-4 text-blue-400" />
      <span>My Videos</span>
      <span className="ml-auto text-xs bg-gray-800 px-2 py-1 rounded-full"></span>
    </Link>
  </div>
</div>

<div className="border-t border-gray-800 my-4"></div>
        <div className="p-4 border-t border-gray-800">
          {isAuthenticated ? (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{user?.email}</p>
                <p className="text-xs text-gray-500">
                  {user?.plan === 'paid' ? 'Pro Plan' : 'Free Plan'}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">Free chats remaining: {5 - (anonymousUsage?.chat || 0)}</p>
              <Link
                to="/login"
                className="text-sm text-purple-400 hover:text-purple-300"
              >
                Sign in for more
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        chats={chats}
        activeChat={activeChat}
        onNewChat={createNewChat}
        onSelectChat={selectChat}
        onDeleteChat={deleteChat}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Chat Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
  <div className="flex items-center space-x-3">
    <button
      onClick={() => setSidebarOpen(true)}
      className="md:hidden p-2 hover:bg-gray-800 rounded-lg transition-colors"
    >
      <Menu className="w-5 h-5" />
    </button>
    <div className="flex items-center space-x-2">
      {activeChat && isAuthenticated ? (
        <input
          type="text"
          value={chatTitle}
          onChange={(e) => setChatTitle(e.target.value)}
          onBlur={() => updateChatTitle(activeChat, chatTitle)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              updateChatTitle(activeChat, chatTitle);
            }
          }}
          className="bg-transparent text-lg font-semibold border-b border-transparent hover:border-gray-600 focus:border-purple-500 focus:outline-none px-1 min-w-[200px]"
        />
      ) : (
        <h2 className="text-lg font-semibold">{chatTitle}</h2>
      )}
      {activeChat && (
        <button
          onClick={() => updateChatTitle(activeChat, chatTitle)}
          className="p-1 hover:bg-gray-800 rounded"
          title="Save title"
        >
          <Check className="w-4 h-4" />
        </button>
      )}
    </div>
  </div>
  <div className="flex items-center space-x-2">
    {/* Download and Share buttons - Add this block */}
    {activeChat && isAuthenticated && (
      <>
        <button
          onClick={downloadChat}
          className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          title="Download chat"
        >
          <DownloadIcon className="w-4 h-4" />
        </button>
        <button
          onClick={shareChat}
          className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          title="Share chat"
        >
          <Share2 className="w-4 h-4" />
        </button>
      </>
    )}
    
    {/* Existing free/pro indicators */}
    {!isAuthenticated && (
      <span className="text-sm text-gray-500">
        Free: {5 - (anonymousUsage?.chat || 0)} left
      </span>
    )}
    {isAuthenticated && user?.plan === 'paid' && (
      <span className="flex items-center space-x-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
        <Crown className="w-3 h-3" />
        <span>Pro</span>
      </span>
    )}
  </div>
</div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-4">
          {messages.length === 0 && !isStreaming ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
  <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center mb-4">
    <img 
      src="https://app.gospelflixstream.com/aigenesis.jpg" 
      alt="AI Genesis Logo"
      className="w-full h-full object-cover"
    />
  </div>
              <h3 className="text-xl font-semibold mb-2">How can I help you today?</h3>
              <p className="text-gray-400 max-w-md mb-6">
                I'm AI Genesis, powered by the Transcendent Open Protocol. Ask me anything!
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
                {[
                  "Explain quantum computing",
                  "Write a poem about AI",
                  "Help me debug this code",
                  "Plan a trip to Japan"
                ].map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMessage(prompt)}
                    className="px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-left transition-colors"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : ''}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
  <img 
    src="https://app.gospelflixstream.com/aigenesis.jpg" 
    alt="AI Assistant"
    className="w-full h-full object-cover"
  />
</div>
                  )}

                  <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                    <div className={`p-4 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-gray-800'
                        : 'bg-gray-800/50'
                    }`}>
                      <ReactMarkdown className="prose prose-invert max-w-none text-sm">
                        {msg.content}
                      </ReactMarkdown>
                    </div>

                    <div className="flex items-center justify-between mt-2 px-1">
                      <span className="text-xs text-gray-500">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                     <button
  onClick={() => {
    navigator.clipboard.writeText(msg.content);
    // Show temporary success feedback
    const btn = document.getElementById(`copy-btn-${index}`);
    if (btn) {
      const originalIcon = btn.innerHTML;
      btn.innerHTML = '<svg class="w-3 h-3 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 6L9 17L4 12" strokeWidth="2"/></svg>';
      setTimeout(() => {
        btn.innerHTML = originalIcon;
      }, 2000);
    }
  }}
  id={`copy-btn-${index}`}
  className="text-xs text-gray-400 hover:text-white transition-colors p-1"
  title="Copy message"
>
  <Copy className="w-3 h-3" />
</button>
                    </div>
                  </div>

                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}

              {/* Streaming Response */}
              {isStreaming && streamingResponse && (
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0">
  <img 
    src="https://app.gospelflixstream.com/aigenesis.jpg" 
    alt="AI Assistant"
    className="w-full h-full object-cover"
  />
</div>

                  <div className="max-w-[80%]">
                    <div className="p-4 rounded-lg bg-gray-800/50">
                      <ReactMarkdown className="prose prose-invert max-w-none text-sm">
                        {streamingResponse}
                      </ReactMarkdown>
                      <div className="flex space-x-1 mt-2">
                        <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"></div>
                        <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(isLoading || isStreaming) && !streamingResponse && (
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
  <img 
    src="https://app.gospelflixstream.com/aigenesis.jpg" 
    alt="AI Assistant"
    className="w-full h-full object-cover"
  />
</div>
                  <div className="p-4 rounded-lg bg-gray-800/50">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-800 p-4">
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Message AI Genesis..."
                className="w-full p-4 bg-gray-800 border border-gray-700 rounded-lg resize-none focus:outline-none focus:border-purple-500 text-sm min-h-[100px] max-h-[200px]"
                rows="3"
                disabled={isLoading || isStreaming}
              />
              <div className="absolute bottom-3 right-3 flex items-center space-x-2">
                <button
                  onClick={() => setMessage('')}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  disabled={!message || isLoading || isStreaming}
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  onClick={sendMessage}
                  disabled={isLoading || isStreaming || !message.trim()}
                  className={`p-2 rounded-lg transition-colors ${
                    isLoading || isStreaming || !message.trim()
                      ? 'bg-gray-700 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {(isLoading || isStreaming) ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="mt-3 text-xs text-gray-500">
              <span>AI Genesis can make mistakes. Consider checking important information.</span>
              {!isAuthenticated && (
                <span className="ml-2">
                  Free chats: {5 - (anonymousUsage?.chat || 0)} remaining
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Enhanced Generate Page (AI Studio)
function GeneratePage({ isAuthenticated, user, anonymousUsage, setAnonymousUsage }) {
  const [activeTab, setActiveTab] = useState('image');
  const [imagePrompt, setImagePrompt] = useState('');
  const [videoPrompt, setVideoPrompt] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageStyle, setImageStyle] = useState('realistic');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [showSignupBanner, setShowSignupBanner] = useState(false);
  const [freeUsage, setFreeUsage] = useState(() => {
    return JSON.parse(localStorage.getItem('freeUsage') || '{"image":0,"video":0}');
  });
  const fileInputRef = useRef(null);

  const styles = [
    { id: 'realistic', name: 'Realistic', icon: '🎨' },
    { id: 'anime', name: 'Anime', icon: '🌸' },
    { id: 'digital-art', name: 'Digital Art', icon: '🖌️' },
    { id: 'photographic', name: 'Photographic', icon: '📷' },
    { id: 'fantasy', name: 'Fantasy', icon: '🐉' },
    { id: 'cyberpunk', name: 'Cyberpunk', icon: '🤖' }
  ];

  const aspectRatios = [
    { id: '1:1', name: 'Square', icon: '⬜' },
    { id: '16:9', name: 'Widescreen', icon: '📺' },
    { id: '9:16', name: 'Portrait', icon: '📱' },
    { id: '4:3', name: 'Standard', icon: '🖥️' }
  ];

  const examplePrompts = [
    "A futuristic city with flying cars at sunset, neon lights, cyberpunk style",
    "A majestic dragon soaring over mystical mountains, fantasy art",
    "An astronaut exploring an alien jungle with bioluminescent plants",
    "A steampunk laboratory with intricate brass machinery and glowing vials",
    "A serene Japanese garden with cherry blossoms and koi pond, anime style",
    "Cyberpunk street market at night with holographic signs and rain reflections"
  ];

  // Show signup banner when component mounts for non-logged in users
  useEffect(() => {
    if (!isAuthenticated) {
      setShowSignupBanner(true);
    }
  }, [isAuthenticated]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        alert('File size must be less than 10MB');
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateImage = async () => {
    if (!imagePrompt.trim()) {
      alert('Please enter a prompt');
      return;
    }
    
    // Check if user is logged in
    if (!isAuthenticated) {
      setShowSignupBanner(true);
      return;
    }
    
    // Check free limits for free accounts
    if (isAuthenticated && user?.plan !== 'paid') {
      // Check if user has used their free image generation
      if (freeUsage.image >= 1) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
        return;
      }
    }
    
    setIsGenerating(true);
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/ai/text-to-image`, {
        prompt: `${imageStyle} style, ${imagePrompt}`,
        width: getWidthFromAspectRatio(aspectRatio),
        height: getHeightFromAspectRatio(aspectRatio),
        style: imageStyle
      }, { 
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      if (response.data.image_url) {
        setGeneratedImage(response.data.image_url);
        
        // Update free usage for free accounts
        if (isAuthenticated && user?.plan !== 'paid') {
          const updatedUsage = { ...freeUsage, image: freeUsage.image + 1 };
          setFreeUsage(updatedUsage);
          localStorage.setItem('freeUsage', JSON.stringify(updatedUsage));
        }
      } else {
        throw new Error('No image URL returned');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      if (error.response?.status === 402) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
      } else {
        alert(error.response?.data?.error || 'Image generation failed. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const generateVideo = async () => {
    if (!videoPrompt.trim() && !selectedImage) {
      alert('Please provide both a prompt and an image');
      return;
    }
    
    // Check if user is logged in
    if (!isAuthenticated) {
      setShowSignupBanner(true);
      return;
    }
    
    // Check free limits for free accounts
    if (isAuthenticated && user?.plan !== 'paid') {
      // Check if user has used their free video generation
      if (freeUsage.video >= 1) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
        return;
      }
    }
    
    setIsGenerating(true);
    const formData = new FormData();
    formData.append('prompt', videoPrompt);
    if (selectedImage) {
      formData.append('image', selectedImage);
    }
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/ai/image-to-video`, formData, { 
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      if (response.data.video_url) {
        setGeneratedVideo(response.data.video_url);
        
        // Update free usage for free accounts
        if (isAuthenticated && user?.plan !== 'paid') {
          const updatedUsage = { ...freeUsage, video: freeUsage.video + 1 };
          setFreeUsage(updatedUsage);
          localStorage.setItem('freeUsage', JSON.stringify(updatedUsage));
        }
      } else {
        throw new Error('No video URL returned');
      }
    } catch (error) {
      console.error('Video generation error:', error);
      if (error.response?.status === 402) {
        setShowLimitWarning(true);
        setTimeout(() => setShowLimitWarning(false), 3000);
      } else {
        alert(error.response?.data?.error || 'Video generation failed. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const getWidthFromAspectRatio = (ratio) => {
    switch (ratio) {
      case '1:1': return 512;
      case '16:9': return 768;
      case '9:16': return 512;
      case '4:3': return 682;
      default: return 512;
    }
  };

  const getHeightFromAspectRatio = (ratio) => {
    switch (ratio) {
      case '1:1': return 512;
      case '16:9': return 432;
      case '9:16': return 910;
      case '4:3': return 512;
      default: return 512;
    }
  };

  const downloadImage = () => {
    if (generatedImage) {
      const link = document.createElement('a');
      link.href = generatedImage;
      link.download = `ai-genesis-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const downloadVideo = () => {
    if (generatedVideo) {
      const link = document.createElement('a');
      link.href = generatedVideo;
      link.download = `ai-genesis-video-${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const copyPrompt = (prompt) => {
    if (activeTab === 'image') {
      setImagePrompt(prompt);
    } else {
      setVideoPrompt(prompt);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setPreviewImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const clearOutput = () => {
    if (activeTab === 'image') {
      setGeneratedImage(null);
    } else {
      setGeneratedVideo(null);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      {/* Signup Banner for non-logged-in users */}
      {!isAuthenticated && showSignupBanner && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4">
          <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <AlertCircle className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="font-medium text-sm">An account is required</p>
                  <p className="text-gray-300 text-xs">Create an account to get started</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Link
                  to="/signup"
                  onClick={() => setShowSignupBanner(false)}
                  className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs"
                >
                  Sign Up
                </Link>
                <button
                  onClick={() => setShowSignupBanner(false)}
                  className="p-1 hover:bg-gray-700/50 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Limit warning for free accounts */}
      {showLimitWarning && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50"
        >
          <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <div>
                <p className="font-medium">Free limit reached!</p>
                <p className="text-sm text-gray-300">Upgrade to continue generating.</p>
              </div>
              <Link 
                to="/pricing" 
                onClick={() => setShowLimitWarning(false)}
                className="ml-4 px-4 py-1 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm"
              >
                Upgrade
              </Link>
            </div>
          </div>
        </motion.div>
      )}
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              AI Studio
            </span>
          </h1>
          <p className="text-gray-400">
            Transform your imagination into stunning visuals and animations
            {!isAuthenticated && (
              <span className="block mt-1 text-yellow-400 text-sm">
                An account is required
              </span>
            )}
            {isAuthenticated && user?.plan !== 'paid' && (
              <span className="block mt-1 text-green-400 text-sm">
                Free: 1 generation available (image OR video)
              </span>
            )}
            {isAuthenticated && user?.plan === 'paid' && (
              <span className="block mt-1 text-green-400 text-sm">
                <Crown className="w-3 h-3 inline mr-1" />
                Pro: Unlimited generations
              </span>
            )}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-gray-800/50 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('image')}
              className={`px-6 py-2 rounded-md font-medium transition-all flex items-center space-x-2 ${
                activeTab === 'image'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Image className="w-4 h-4" />
              <span>Text to Image</span>
            </button>
            <button
              onClick={() => setActiveTab('video')}
              className={`px-6 py-2 rounded-md font-medium transition-all flex items-center space-x-2 ${
                activeTab === 'video'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Video className="w-4 h-4" />
              <span>Image to Video</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Panel - Controls */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
              {activeTab === 'image' ? (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-3">Image Prompt</label>
                    <textarea
                      value={imagePrompt}
                      onChange={(e) => setImagePrompt(e.target.value)}
                      className="w-full p-4 bg-gray-800 border border-gray-700 rounded-lg resize-none focus:outline-none focus:border-purple-500 text-sm min-h-[120px]"
                      placeholder="Describe the image you want to generate in detail..."
                      rows="4"
                      disabled={!isAuthenticated}
                    />
                    {!isAuthenticated && (
                      <div className="text-xs text-yellow-400 mt-2">
                        Please sign up or log in to generate images
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium mb-3">Style</label>
                      <div className="grid grid-cols-3 gap-2">
                        {styles.map((style) => (
                          <button
                            key={style.id}
                            onClick={() => setImageStyle(style.id)}
                            className={`p-3 rounded-lg transition-all text-sm ${
                              imageStyle === style.id
                                ? 'bg-purple-600/20 border border-purple-500/50'
                                : 'bg-gray-800 hover:bg-gray-700'
                            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!isAuthenticated}
                          >
                            <div className="text-lg mb-1">{style.icon}</div>
                            <div className="text-xs">{style.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-3">Aspect Ratio</label>
                      <div className="grid grid-cols-2 gap-2">
                        {aspectRatios.map((ratio) => (
                          <button
                            key={ratio.id}
                            onClick={() => setAspectRatio(ratio.id)}
                            className={`p-3 rounded-lg transition-all text-sm ${
                              aspectRatio === ratio.id
                                ? 'bg-purple-600/20 border border-purple-500/50'
                                : 'bg-gray-800 hover:bg-gray-700'
                            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!isAuthenticated}
                          >
                            <div className="text-lg mb-1">{ratio.icon}</div>
                            <div className="text-xs">{ratio.name}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={generateImage}
                    disabled={isGenerating || !imagePrompt.trim() || !isAuthenticated}
                    className={`w-full py-3 rounded-lg font-medium transition-colors mb-4 ${
                      isGenerating || !imagePrompt.trim() || !isAuthenticated
                        ? 'bg-gray-700 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Generating Image...</span>
                      </span>
                    ) : !isAuthenticated ? (
                      <span className="flex items-center justify-center space-x-2">
                        <LogIn className="w-5 h-5" />
                        <span>Sign In to Generate</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center space-x-2">
                        <Sparkles className="w-5 h-5" />
                        <span>Generate Image</span>
                      </span>
                    )}
                  </button>
                  
                  {isAuthenticated && user?.plan !== 'paid' && (
                    <div className="text-center text-sm text-gray-400">
                      Free image generations remaining: {1 - freeUsage.image}/1
                    </div>
                  )}
                  {!isAuthenticated && (
                    <div className="text-center">
                      <Link
                        to="/signup"
                        className="text-purple-400 hover:text-purple-300 text-sm font-medium"
                      >
                        An account is required →
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-3">Video Prompt</label>
                    <textarea
                      value={videoPrompt}
                      onChange={(e) => setVideoPrompt(e.target.value)}
                      className="w-full p-4 bg-gray-800 border border-gray-700 rounded-lg resize-none focus:outline-none focus:border-purple-500 text-sm min-h-[120px]"
                      placeholder="Describe the animation or movement you want..."
                      rows="4"
                      disabled={!isAuthenticated}
                    />
                    {!isAuthenticated && (
                      <div className="text-xs text-yellow-400 mt-2">
                        Please sign up or log in to generate videos
                      </div>
                    )}
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-3">Upload Image</label>
                    <div 
                      onClick={() => isAuthenticated && fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                        !isAuthenticated 
                          ? 'border-gray-700 opacity-50 cursor-not-allowed' 
                          : 'border-gray-700 hover:border-purple-500'
                      }`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={!isAuthenticated}
                      />
                      {previewImage ? (
                        <div className="relative">
                          <img
                            src={previewImage}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              clearImage();
                            }}
                            className="absolute top-2 right-2 p-2 bg-red-500/20 text-red-400 rounded-full hover:bg-red-500/30"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                          <p className="text-gray-400 mb-2">
                            {!isAuthenticated ? 'Sign in to upload images' : 'Click to upload an image'}
                          </p>
                          <p className="text-gray-500 text-sm">or drag and drop</p>
                          <p className="text-gray-500 text-sm mt-2">PNG, JPG, GIF up to 10MB</p>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={generateVideo}
                    disabled={isGenerating || !videoPrompt.trim() || !selectedImage || !isAuthenticated}
                    className={`w-full py-3 rounded-lg font-medium transition-colors ${
                      isGenerating || !videoPrompt.trim() || !selectedImage || !isAuthenticated
                        ? 'bg-gray-700 cursor-not-allowed'
                        : 'bg-purple-600 hover:bg-purple-700'
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Generating Video...</span>
                      </span>
                    ) : !isAuthenticated ? (
                      <span className="flex items-center justify-center space-x-2">
                        <LogIn className="w-5 h-5" />
                        <span>Sign In to Generate</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center space-x-2">
                        <Film className="w-5 h-5" />
                        <span>Generate Video</span>
                      </span>
                    )}
                  </button>
                  
                  {isAuthenticated && user?.plan !== 'paid' && (
                    <div className="text-center text-sm text-gray-400 mt-4">
                      Free video generations remaining: {1 - freeUsage.video}/1
                    </div>
                  )}
                  {!isAuthenticated && (
                    <div className="text-center mt-4">
                      <Link
                        to="/signup"
                        className="text-purple-400 hover:text-purple-300 text-sm font-medium"
                      >
                        An Account is required →
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          
          {/* Output Preview */}
          <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium">Generated Result</h3>
              {(generatedImage || generatedVideo) && (
                <button
                  onClick={clearOutput}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Clear
                </button>
              )}
            </div>
            
            {generatedImage && isAuthenticated && (
  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <CheckCircle className="w-5 h-5 text-green-400" />
        <div>
          <p className="font-medium text-green-400">Image generated successfully!</p>
          <p className="text-sm text-gray-400">Your image has been saved to your gallery.</p>
        </div>
      </div>
      <Link
        to="/images"
        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
      >
        <Image className="w-4 h-4" />
        <span>View My Gallery</span>
      </Link>
    </div>
  </div>
)}

{generatedVideo && isAuthenticated && (
  <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <CheckCircle className="w-5 h-5 text-green-400" />
        <div>
          <p className="font-medium text-green-400">Video generated successfully!</p>
          <p className="text-sm text-gray-400">Your video has been saved to your gallery.</p>
        </div>
      </div>
      <Link
        to="/videos"
        className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
      >
        <Film className="w-4 h-4" />
        <span>View My Gallery</span>
      </Link>
    </div>
  </div>
)}
            
            <div className="aspect-square bg-gray-800/50 rounded-lg border border-gray-700 flex items-center justify-center overflow-hidden">
              {activeTab === 'image' ? (
                generatedImage ? (
                  <div className="relative w-full h-full">
                    <img
                      src={generatedImage}
                      alt="Generated"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-3 right-3 flex space-x-2">
                      <button
                        onClick={downloadImage}
                        className="p-2 bg-gray-900/80 hover:bg-gray-800 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigator.clipboard.writeText(generatedImage)}
                        className="p-2 bg-gray-900/80 hover:bg-gray-800 rounded-lg transition-colors"
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-8">
                    <Image className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm">No image generated yet</p>
                    <p className="text-gray-600 text-xs mt-1">Your image will appear here</p>
                  </div>
                )
              ) : (
                generatedVideo ? (
                  <div className="relative w-full h-full">
                    <video
                      src={generatedVideo}
                      controls
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-3 right-3">
                      <button
                        onClick={downloadVideo}
                        className="p-2 bg-gray-900/80 hover:bg-gray-800 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center p-8">
                    <Video className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm">No video generated yet</p>
                    <p className="text-gray-600 text-xs mt-1">Your video will appear here</p>
                  </div>
                )
              )}
            </div>
            
            {(generatedImage || generatedVideo) && (
              <div className="mt-4 text-sm text-gray-400">
                <div className="flex items-center justify-between">
                  <span>Status:</span>
                  <span className="text-green-400 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Ready
                  </span>
                </div>
                {isAuthenticated && user?.plan !== 'paid' && activeTab === 'image' && (
                  <div className="mt-2 text-yellow-400">
                    <span className="flex items-center">
                      <Info className="w-3 h-3 mr-1" />
                      You have used your free image generation
                    </span>
                  </div>
                )}
                {isAuthenticated && user?.plan !== 'paid' && activeTab === 'video' && (
                  <div className="mt-2 text-yellow-400">
                    <span className="flex items-center">
                      <Info className="w-3 h-3 mr-1" />
                      You have used your free video generation
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Examples & Output */}
          <div className="space-y-8">
            {/* Example Prompts */}
            <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
              <h3 className="text-sm font-medium mb-4 flex items-center">
                <Sparkles className="w-4 h-4 mr-2 text-purple-400" />
                Example Prompts
              </h3>
              <div className="space-y-3">
                {examplePrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => copyPrompt(prompt)}
                    className="w-full text-left p-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group"
                  >
                    <div className="text-sm line-clamp-2">{prompt}</div>
                    <div className="text-xs text-gray-500 mt-1 group-hover:text-purple-400">
                      Click to use
                    </div>
                  </button>
                ))}
              </div>
              {!isAuthenticated && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <p className="text-xs text-yellow-400">
                    <Info className="w-3 h-3 inline mr-1" />
                    Sign up to use these prompts for generation
                  </p>
                </div>
              )}
            </div>
            
            {/* Usage Stats */}
            <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
              <h3 className="text-sm font-medium mb-4 flex items-center">
                <BarChart3 className="w-4 h-4 mr-2 text-blue-400" />
                Usage Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-400">Account Status:</span>
                  <span className={`text-sm font-medium ${
                    !isAuthenticated ? 'text-yellow-400' : 
                    user?.plan === 'paid' ? 'text-green-400' : 'text-blue-400'
                  }`}>
                    {!isAuthenticated ? 'Not Logged In' : 
                     user?.plan === 'paid' ? 'Premium' : 'Free'}
                  </span>
                </div>
                
                {isAuthenticated && user?.plan !== 'paid' && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Images Used:</span>
                      <span className="text-sm">{freeUsage.image}/1</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Videos Used:</span>
                      <span className="text-sm">{freeUsage.video}/1</span>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-700">
                      <Link
                        to="/pricing"
                        className="block text-center px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
                      >
                        Upgrade to Unlimited
                      </Link>
                    </div>
                  </>
                )}
                
                {!isAuthenticated && (
                  <div className="mt-4 space-y-3">
                    <Link
                      to="/signup"
                      className="block text-center px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
                    >
                      Sign Up for Free
                    </Link>
                    <Link
                      to="/login"
                      className="block text-center px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                    >
                      Log In
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
// Simplified Home Page
function HomePage({ isAuthenticated, user }) {
  if (isAuthenticated && user?.plan === 'paid') {
    return <Navigate to="/chat" />;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4">
      <div className="max-w-3xl text-center">
        <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full mb-6">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium">Transcendent Open Protocol</span>
        </div>

        <h1 className="text-5xl font-bold mb-4">
          <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
            AI Genesis
          </span>
        </h1>

        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
          Experience the future of artificial intelligence with our Transcendent Open Protocol platform.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/chat"
            className="px-8 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
          >
            Start Chatting Free
          </Link>

          <Link
            to="/pricing"
            className="px-8 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-medium transition-colors"
          >
            View Pricing
          </Link>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="w-12 h-12 rounded-lg bg-purple-600/20 flex items-center justify-center mb-4 mx-auto">
              <MessageSquare className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="font-semibold mb-2">AI Chat</h3>
            <p className="text-sm text-gray-400">Intelligent conversations with our advanced AI</p>
          </div>

          <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center mb-4 mx-auto">
              <Image className="w-6 h-6 text-blue-400" />
            </div>
            <h3 className="font-semibold mb-2">Image Generation</h3>
            <p className="text-sm text-gray-400">Create stunning visuals from text descriptions</p>
          </div>

          <div className="p-6 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="w-12 h-12 rounded-lg bg-green-600/20 flex items-center justify-center mb-4 mx-auto">
              <Video className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="font-semibold mb-2">Video Creation</h3>
            <p className="text-sm text-gray-400">Transform images into animated videos</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simplified Login Page
function LoginPage({ login }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');
    setShowResendButton(false);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      if (response.data.success) {
        // Store token and user data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        // Redirect to chat
        window.location.href = '/chat';
      }
    } catch (err) {
      const errorData = err.response?.data;
      
      if (errorData?.error === 'EMAIL_NOT_VERIFIED') {
        setError('Please verify your email address before logging in.');
        setShowResendButton(true);
        
        // Show helpful message
        setResendMessage('Check your email for the verification link. If you didn\'t receive it, check your spam folder.');
      } else {
        setError(errorData?.error || 'An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const response = await axios.post(`${API_URL}/auth/resend-verification`, {
        email
      });
      
      if (response.data.success) {
        setResendMessage('Verification email has been resent. Please check your inbox and spam folder.');
        setShowResendButton(false);
        setError('');
      }
    } catch (err) {
      const errorData = err.response?.data;
      setResendMessage(errorData?.error || 'Failed to resend verification email');
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-8">
          <div className="text-center mb-8">
            <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center mx-auto mb-4">
              <img 
                src="https://app.gospelflixstream.com/aigenesis.jpg" 
                alt="AI Genesis"
                className="w-full h-full object-cover"
              />
            </div>
            <h2 className="text-xl font-semibold">Welcome to AI Genesis</h2>
            <p className="text-gray-400 mt-2 text-sm">Sign in to continue</p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center space-x-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {resendMessage && (
            <div className={`mb-6 p-3 rounded-lg border text-sm ${
              resendMessage.includes('resent') 
                ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
            }`}>
              {resendMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
                  placeholder="you@example.com"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
              </div>

              {showResendButton && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isLoading}
                    className="text-sm text-purple-400 hover:text-purple-300 font-medium underline"
                  >
                    Resend verification email
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-lg font-medium transition-colors ${
                  isLoading
                    ? 'bg-gray-700 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-700">
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-gray-400 text-sm">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-purple-400 hover:text-purple-300 font-medium">
                    Create one
                  </Link>
                </p>
              </div>
              
              <div className="text-center">
                <p className="text-gray-400 text-sm">
                  Or continue as{' '}
                  <Link to="/chat" className="text-purple-400 hover:text-purple-300 font-medium">
                    Guest
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// Registration/Signup Page Component
// Registration/Signup Page Component
// Updated SignupPage Component - NO VERIFICATION
function SignupPage({ login }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { themeStyles } = React.useContext(ThemeContext);

  const validateForm = () => {
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;
    
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/register`, {
        email,
        password
      });
      
      if (response.data.success) {
        setSuccess(true);
        
        // Auto-login after registration
        setTimeout(async () => {
          try {
            const loginResult = await login(email, password);
            if (loginResult.success) {
              navigate('/chat');
            } else {
              navigate('/login');
            }
          } catch (err) {
            setError('Registration successful but auto-login failed.');
            setSuccess(false);
          }
        }, 1500);
      } else {
        setError(response.data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
        <div className="w-full max-w-md text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}
          >
            <div className={`w-16 h-16 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-4`}>
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-4">Welcome!</h2>
            <p className={`${themeStyles.secondaryText} mb-4`}>Your account has been created successfully.</p>
            <div className="flex space-x-1 justify-center">
              <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`}></div>
              <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.1s' }}></div>
              <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }}></div>
            </div>
            <p className={`text-sm ${themeStyles.secondaryText} mt-4`}>Logging you in...</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}>
          <div className="text-center mb-8">
            <div className={`w-16 h-16 rounded-xl overflow-hidden mx-auto mb-4 shadow-md`}>
              <img 
                src="https://app.gospelflixstream.com/bibleai.png" 
                alt="Bible AI"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/64/amber-600/white?text=Bible';
                }}
              />
            </div>
            <h2 className="text-2xl font-bold mb-2">Create Account</h2>
            <p className={`${themeStyles.secondaryText}`}>Join us in exploring faith</p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="you@example.com"
                  required
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="••••••••"
                  required
                  minLength="6"
                  disabled={isLoading}
                />
                <p className={`text-xs ${themeStyles.secondaryText} mt-1`}>Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full p-3 ${themeStyles.input} rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm ${themeStyles.text} shadow-inner`}
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 rounded-xl font-medium transition-colors shadow-md hover:shadow-lg ${
                  isLoading
                    ? `${themeStyles.secondaryBg} cursor-not-allowed`
                    : `${themeStyles.button} text-white`
                }`}
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-8 border-t ${themeStyles.border}">
            <div className="space-y-4">
              <div className="text-center">
                <p className={`${themeStyles.secondaryText} text-sm`}>
                  Already have an account?{' '}
                  <Link to="/login" className={`${themeStyles.accent} hover:underline font-medium`}>
                    Sign in
                  </Link>
                </p>
              </div>
              
              <div className="text-center">
                <p className={`${themeStyles.secondaryText} text-sm`}>
                  Or continue as{' '}
                  <Link to="/chat" className={`${themeStyles.accent} hover:underline font-medium`}>
                    Guest
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Simplified Profile Page
function ProfilePage({ user, logout }) {
  const isPaidUser = user?.plan === 'paid';

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold mb-8">Profile</h1>

        <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
              <User className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">{user?.email}</h2>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm mt-2 ${
                isPaidUser
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-gray-700 text-gray-400'
              }`}>
                {isPaidUser ? (
                  <>
                    <Crown className="w-3 h-3 mr-1" />
                    Lifetime Pro
                  </>
                ) : 'Free Plan'}
              </div>
            </div>
          </div>
          
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Link
              to="/images"
              className="bg-gray-800 p-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center space-x-2 mb-2">
                <Image className="w-5 h-5 text-purple-400" />
                <h3 className="font-medium">Images</h3>
              </div>
              <p className="text-sm text-gray-400">View generated images</p>
            </Link>
            
            <Link
              to="/videos"
              className="bg-gray-800 p-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center space-x-2 mb-2">
                <Film className="w-5 h-5 text-blue-400" />
                <h3 className="font-medium">Videos</h3>
              </div>
              <p className="text-sm text-gray-400">View generated videos</p>
            </Link>
            
            <Link
              to="/chat"
              className="bg-gray-800 p-4 rounded-lg hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-center space-x-2 mb-2">
                <MessageSquare className="w-5 h-5 text-green-400" />
                <h3 className="font-medium">Chats</h3>
              </div>
              <p className="text-sm text-gray-400">View chat history</p>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-800 p-4 rounded-lg">
              <div className="text-sm text-gray-400 mb-1">Member Since</div>
              <div className="font-medium">
                {new Date(user?.created_at).toLocaleDateString()}
              </div>
            </div>
            {isPaidUser && (
              <div className="bg-gray-800 p-4 rounded-lg">
                <div className="text-sm text-gray-400 mb-1">Upgraded On</div>
                <div className="font-medium">
                  {new Date(user?.plan_purchased_at).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            {!isPaidUser && (
              <Link
                to="/pricing"
                className="block w-full py-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-center font-medium transition-colors"
              >
                Upgrade to Pro
              </Link>
            )}
            <button
              onClick={logout}
              className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Privacy/Terms Page Component
function PrivacyPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/privacy')
      .then(res => res.json())
      .then(data => {
        setContent(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load privacy content:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <p className="text-gray-400">Failed to load content</p>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">{content.title}</h1>
          <p className="text-gray-400">Last updated: {content.lastUpdated}</p>
        </div>

        <div className="space-y-12">
          {/* Privacy Policy */}
          <div>
            <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-gray-800">
              Privacy Policy
            </h2>
            <div className="space-y-6">
              {content.privacy.map((section, index) => (
                <div key={index} className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
                  <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
                  <div className="text-gray-300 whitespace-pre-line text-sm leading-relaxed">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Terms of Service */}
          <div>
            <h2 className="text-2xl font-bold mb-6 pb-2 border-b border-gray-800">
              Terms of Service
            </h2>
            <div className="space-y-6">
              {content.terms.map((section, index) => (
                <div key={index} className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
                  <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
                  <div className="text-gray-300 whitespace-pre-line text-sm leading-relaxed">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="space-y-3">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-purple-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">General Inquiries</p>
                  <a 
                    href={`mailto:${content.contact.email}`}
                    className="text-purple-400 hover:text-purple-300 transition-colors"
                  >
                    {content.contact.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center">
                <Shield className="w-5 h-5 text-green-400 mr-3" />
                <div>
                  <p className="text-sm text-gray-400">Privacy Concerns</p>
                  <a 
                    href={`mailto:${content.contact.privacyEmail}`}
                    className="text-green-400 hover:text-green-300 transition-colors"
                  >
                    {content.contact.privacyEmail}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Contact Page Component
function ContactPage() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/contact')
      .then(res => res.json())
      .then(data => {
        setContent(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load contact info:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <p className="text-gray-400">Failed to load contact information</p>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold mb-2">{content.title}</h1>
          <p className="text-gray-400">{content.description}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {content.contactMethods.map((method, index) => (
            <div key={index} className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-gray-800/50 flex items-center justify-center flex-shrink-0">
                  {method.type.includes('Support') ? (
                    <HelpCircle className="w-6 h-6 text-blue-400" />
                  ) : method.type.includes('Privacy') ? (
                    <Shield className="w-6 h-6 text-green-400" />
                  ) : method.type.includes('Business') ? (
                    <TrendingUp className="w-6 h-6 text-yellow-400" />
                  ) : (
                    <Mail className="w-6 h-6 text-purple-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{method.type}</h3>
                  <a 
                    href={`mailto:${method.email}`}
                    className="text-purple-400 hover:text-purple-300 transition-colors block mb-2"
                  >
                    {method.email}
                  </a>
                  <p className="text-sm text-gray-400">
                    Response time: {method.responseTime}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-8 text-center">
          <h3 className="text-lg font-semibold mb-4">How to Reach Us</h3>
          <p className="text-gray-300 mb-4">
            For the fastest response, please use the appropriate email address above and include relevant details.
          </p>
          <div className="space-y-2 text-sm text-gray-400">
            <p>{content.hours}</p>
            <p>{content.note}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Simplified Pricing Page
function PricingPage() {
  const navigate = useNavigate();
  const { themeStyles } = React.useContext(ThemeContext);
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: 'forever',
      features: [
        '5 AI Chat Messages',
        '1 Image Generation',
        '1 Video Generation',
        'Basic Support'
      ],
      cta: 'Current Plan'
    },
    {
      id: 'lifetime',
      name: 'Lifetime Pro',
      price: '$14.99',
      period: 'one-time',
      features: [
        'Unlimited AI Chat',
        'Unlimited Image Generation',
        'Unlimited Video Generation',
        'Priority Support',
        'Chat History',
        'All Future Features'
      ],
      cta: 'Get Lifetime Access',
      popular: true
    }
  ];

const handlePayment = async (planId, currency) => {
  if (planId === 'free') {
    navigate('/chat');
    return;
  }
  
  setLoading(true);
  setProcessingPlan(planId);
  
  try {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));
    
    if (!token || !user) {
      alert('Please login first');
      navigate('/login');
      return;
    }
    
    console.log('=== Starting Payment Process ===');
    console.log('Plan ID:', planId);
    console.log('Currency:', currency);
    console.log('User Email:', user.email);
    
    // Initialize payment on backend - EXACT match to BibleAI
    const response = await axios.post(`${API_URL}/payment/paystack-initiate`, {
      email: user.email,
      currency: currency
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    console.log('Init response:', JSON.stringify(response.data, null, 2));
    
    // Check if response contains public_key
    if (!response.data.public_key) {
      console.error('ERROR: No public_key in response!');
      alert('Payment configuration error. Please contact support.');
      setLoading(false);
      setProcessingPlan(null);
      return;
    }
    
    console.log('Public key received:', response.data.public_key);
    
    if (response.data.success && response.data.access_code) {
      const paystackPublicKey = response.data.public_key;
      const currentReference = response.data.reference;
      
      console.log('Access code:', response.data.access_code);
      console.log('Reference (from PayStack):', currentReference);
      
      // Set amount for PayStack - USD only (1500 cents = $15.00)
      const amountForPaystack = 1500;
      
      console.log(`Initializing PayStack with amount: ${amountForPaystack} ${currency}`);
      
      // IMPORTANT: EXACT SAME PayStack options as BibleAI
      const paystackOptions = {
        key: paystackPublicKey,
        email: user.email,
        amount: amountForPaystack,
        currency: 'USD',
        ref: currentReference,
        callback: async (paymentResponse) => {
          console.log('Payment callback:', paymentResponse);
          
          const processingMsg = document.createElement('div');
          processingMsg.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50';
          processingMsg.innerHTML = `
            <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
              <div class="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p class="font-semibold text-gray-800">Verifying payment...</p>
              <p class="text-sm text-gray-600 mt-2">Please wait while we upgrade your account</p>
            </div>
          `;
          document.body.appendChild(processingMsg);
          
          try {
            const verifyResponse = await axios.post(`${API_URL}/payment/paystack-verify`, {
              reference: paymentResponse.reference || paymentResponse.trxref,
              planId: planId
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('Verify response:', verifyResponse.data);
            
            if (verifyResponse.data.success) {
              if (verifyResponse.data.user) {
                localStorage.setItem('user', JSON.stringify(verifyResponse.data.user));
              }
              
              processingMsg.innerHTML = `
                <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                  <div class="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <p class="font-semibold text-green-600">Payment Successful!</p>
                  <p class="text-sm text-gray-600 mt-2">You now have Lifetime Pro access!</p>
                  <p class="text-xs text-gray-500 mt-4">Redirecting to chat...</p>
                </div>
              `;
              
              setTimeout(() => {
                processingMsg.remove();
                window.location.href = '/chat';
              }, 2000);
            } else {
              throw new Error(verifyResponse.data.error || 'Verification failed');
            }
          } catch (error) {
            console.error('Verification error:', error);
            processingMsg.innerHTML = `
              <div class="bg-white rounded-xl p-6 text-center max-w-md mx-4">
                <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </div>
                <p class="font-semibold text-red-600">Verification Failed</p>
                <p class="text-sm text-gray-600 mt-2">${error.message}</p>
                <button onclick="window.location.href='/pricing'" class="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm">
                  Try Again
                </button>
              </div>
            `;
          }
        },
        onClose: () => {
          console.log('Payment window closed');
          setLoading(false);
          setProcessingPlan(null);
        },
        onError: (error) => {
          console.error('Payment error:', error);
          alert('Payment failed. Please try again.');
          setLoading(false);
          setProcessingPlan(null);
        }
      };
      
      // EXACT SAME PayStack loading logic as BibleAI
      if (window.PaystackPop) {
        const paystack = new window.PaystackPop();
        paystack.newTransaction(paystackOptions);
      } else {
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v1/inline.js';
        script.onload = () => {
          const paystack = new window.PaystackPop();
          paystack.newTransaction(paystackOptions);
        };
        document.body.appendChild(script);
      }
    } else {
      alert(response.data.error || 'Failed to initialize payment');
      setLoading(false);
      setProcessingPlan(null);
    }
  } catch (error) {
    console.error('Payment error:', error);
    alert(error.response?.data?.error || 'Payment failed. Please try again.');
    setLoading(false);
    setProcessingPlan(null);
  }
};


  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 ${themeStyles.background}`}>
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold mb-4">Simple, Transparent, No Monthly Charges!</h1>
          <p className={themeStyles.secondaryText}>Choose the plan that works for you</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`rounded-xl border ${
                plan.popular
                  ? `border-purple-500 ${themeStyles.card} shadow-xl`
                  : `${themeStyles.border} ${themeStyles.card} shadow-md`
              }`}
            >
              {plan.popular && (
                <div className={`px-4 py-2 bg-gradient-to-r ${themeStyles.gradient || 'from-purple-600 to-purple-800'} text-white text-center rounded-t-xl font-medium`}>
                  MOST POPULAR
                </div>
              )}
              
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                  <div className="flex items-baseline mb-2">
                    <span className="text-3xl font-bold">{plan.price}</span>
                    <span className={`${themeStyles.secondaryText} ml-2`}>/{plan.period}</span>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm">
                        <Check className={`w-4 h-4 text-green-600 mr-3`} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {plan.id === 'free' ? (
                  <button
                    onClick={() => handlePayment(plan.id)}
                    className={`w-full py-3 rounded-xl font-medium transition-all shadow-md hover:shadow-lg ${
                      themeStyles.secondaryBg
                    } border ${themeStyles.border} hover:${themeStyles.hover}`}
                  >
                    {plan.cta}
                  </button>
                ) : (
                  <button
                    onClick={() => handlePayment(plan.id)}
                    disabled={loading && processingPlan === plan.id}
                    className={`w-full py-3 rounded-xl font-medium transition-all shadow-md hover:shadow-lg ${
                      loading && processingPlan === plan.id
                        ? 'opacity-50 cursor-not-allowed'
                        : plan.popular
                        ? `${themeStyles.button} text-white`
                        : `${themeStyles.secondaryBg} border ${themeStyles.border} hover:${themeStyles.hover}`
                    }`}
                  >
                    {loading && processingPlan === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex flex-col items-center">
                        <span>💳 Pay $14.99 USD (Lifetime)</span>
                        <span className="text-xs opacity-90">Secure payment via PayStack</span>
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className={`mt-12 text-center ${themeStyles.secondaryText} text-sm`}>
          <p>All plans include access to AI Genesis powered by the Transcendent Open Protocol</p>
          <p className="text-xs mt-2">
            Secure payments via PayStack • Visa • Mastercard • Bank Transfer
          </p>
        </div>
      </div>
    </div>
  );
}


// Payment Callback Handler Component
function PaymentCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { themeStyles } = React.useContext(ThemeContext);
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Verifying your payment...');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const reference = params.get('reference') || params.get('trxref');
        
        console.log('Payment callback reference:', reference);
        
        if (!reference) {
          setStatus('error');
          setMessage('No payment reference found');
          return;
        }
        
        const token = localStorage.getItem('token');
        if (!token) {
          setStatus('error');
          setMessage('Please login to verify your payment');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }
        
        const response = await axios.post(`${API_URL}/payment/paystack-verify`, {
          reference: reference,
          planId: 'lifetime'
        });
        
        if (response.data.success) {
          setStatus('success');
          setMessage('Payment successful! You now have Lifetime Pro access!');
          
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
          
          setTimeout(() => {
            navigate('/chat');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.error || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage('Payment verification failed. Please contact support.');
      }
    };
    
    verifyPayment();
  }, [location, navigate]);

  return (
    <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
      <div className="w-full max-w-md text-center">
        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}>
          {status === 'processing' && (
            <>
              <div className={`w-16 h-16 rounded-xl ${themeStyles.accentBg} border ${themeStyles.accentBorder} flex items-center justify-center mx-auto mb-4`}>
                <RefreshCw className={`w-8 h-8 ${themeStyles.accent} animate-spin`} />
              </div>
              <h2 className="text-2xl font-bold mb-4">Verifying Payment</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="flex space-x-1 justify-center mt-6">
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.1s' }}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }}></div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-green-600">Payment Successful!</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">
                  <Crown className="w-4 h-4 inline mr-1" />
                  You now have Lifetime Pro access!
                </p>
              </div>
              <p className={`text-sm ${themeStyles.secondaryText} mt-4`}>
                Redirecting you to chat...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-red-100 border border-red-200 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600">Verification Error</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className={`block w-full py-3 ${themeStyles.button} text-white rounded-xl font-medium transition-colors shadow-md`}
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className={`block w-full py-3 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl font-medium transition-colors shadow-sm`}
                >
                  Contact Support
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// Add this new component for PayPal success handling
function PaymentSuccessPage() {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your payment...');
  const navigate = useNavigate();
  const location = useLocation();
  const { themeStyles } = React.useContext(ThemeContext);

  useEffect(() => {
    const processPayment = async () => {
      try {
        // Get query parameters
        const params = new URLSearchParams(location.search);
        const token = params.get('token');
        const PayerID = params.get('PayerID');
        const userId = params.get('user');

        // Get the stored order ID
        const orderId = localStorage.getItem('pendingOrder');
        
        if (!orderId || !token || !PayerID) {
          setStatus('error');
          setMessage('Missing payment information. Please contact support.');
          return;
        }

        // Get auth token
        const authToken = localStorage.getItem('token');
        if (!authToken) {
          setStatus('error');
          setMessage('You need to be logged in. Redirecting to login...');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Capture the payment
        const response = await axios.post(
          `${API_URL}/payment/capture-order`,
          { orderId },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );

        if (response.data.success) {
          setStatus('success');
          setMessage('Payment successful! You now have Lifetime Pro access!');
          
          // Update user data in localStorage
          if (response.data.user) {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
          
          // Clear pending order
          localStorage.removeItem('pendingOrder');
          
          // Redirect to chat after 3 seconds
          setTimeout(() => navigate('/chat'), 3000);
        } else {
          setStatus('error');
          setMessage('Payment processing failed. Please contact support.');
        }
      } catch (error) {
        console.error('Payment processing error:', error);
        setStatus('error');
        setMessage(error.response?.data?.error || 'Payment processing failed. Please contact support.');
      }
    };

    processPayment();
  }, [location, navigate]);

  return (
    <div className={`min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 ${themeStyles.background}`}>
      <div className="w-full max-w-md text-center">
        <div className={`${themeStyles.card} rounded-xl border ${themeStyles.border} p-8 shadow-xl`}>
          {status === 'processing' && (
            <>
              <div className={`w-16 h-16 rounded-xl ${themeStyles.accentBg} border ${themeStyles.accentBorder} flex items-center justify-center mx-auto mb-4`}>
                <RefreshCw className={`w-8 h-8 ${themeStyles.accent} animate-spin`} />
              </div>
              <h2 className="text-2xl font-bold mb-4">Processing Payment</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="flex space-x-1 justify-center mt-6">
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.1s' }}></div>
                <div className={`w-2 h-2 ${themeStyles.accent} rounded-full animate-pulse`} style={{ animationDelay: '0.2s' }}></div>
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-green-600">Payment Successful!</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-600">
                  <Crown className="w-4 h-4 inline mr-1" />
                  You now have Lifetime Pro access!
                </p>
              </div>
              <p className={`text-sm ${themeStyles.secondaryText} mt-4`}>
                Redirecting you to chat...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-xl bg-red-100 border border-red-200 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600">Payment Error</h2>
              <p className={themeStyles.secondaryText}>{message}</p>
              <div className="mt-6 space-y-3">
                <button
                  onClick={() => navigate('/pricing')}
                  className={`block w-full py-3 ${themeStyles.button} text-white rounded-xl font-medium transition-colors shadow-md`}
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className={`block w-full py-3 ${themeStyles.secondaryBg} border ${themeStyles.border} rounded-xl font-medium transition-colors shadow-sm`}
                >
                  Contact Support
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Main App Component
// Main App Component - UPDATED with proper theme management
function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });
  const [anonymousUsage, setAnonymousUsage] = useState(() => {
    const saved = localStorage.getItem('anonymousUsage');
    return saved ? JSON.parse(saved) : { chat: 0, image: 0, video: 0 };
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      fetchUserData();
    }
    
    // Apply theme to document
    applyTheme(theme);
  }, []);

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const applyTheme = (currentTheme) => {
    // Save to localStorage
    localStorage.setItem('theme', currentTheme);
    
    // Apply to document root for global CSS variables
    document.documentElement.className = currentTheme;
    document.documentElement.style.colorScheme = currentTheme;
    
    // Apply meta theme-color for mobile browsers
    let metaThemeColor = document.querySelector('meta[name=theme-color]');
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta');
      metaThemeColor.name = 'theme-color';
      document.head.appendChild(metaThemeColor);
    }
    metaThemeColor.content = currentTheme === 'dark' ? '#111827' : '#F9FAFB';
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API_URL}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setIsAuthenticated(true);
      setUser(response.data.user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Authentication failed'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    window.location.href = '/';
  };

  // FIXED: Proper toggle function that actually changes the theme
  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'dark' ? 'light' : 'dark';
      console.log(`Toggling theme from ${prevTheme} to ${newTheme}`); // Debug log
      return newTheme;
    });
  };

  // Get theme styles based on current theme
  const themeStyles = themes[theme] || themes.dark;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, themeStyles }}>
  <Router>
    <div className={`min-h-screen ${themeStyles.background} ${themeStyles.text}`}>
      <Navbar
        isAuthenticated={isAuthenticated}
        user={user}
        logout={logout}
      />
<ThemeTransition theme={theme} animationType="left-right">
          <Routes>
            <Route path="/" element={
              isAuthenticated && user?.plan === 'paid' ?
                <Navigate to="/chat" /> :
                <HomePage isAuthenticated={isAuthenticated} user={user} themeStyles={themeStyles} />
            } />
            <Route path="/login" element={
              isAuthenticated ?
                <Navigate to={user?.plan === 'paid' ? "/chat" : "/"} /> :
                <LoginPage login={login} themeStyles={themeStyles} />
            } />
            <Route path="/signup" element={
              isAuthenticated ?
                <Navigate to="/chat" /> :
                <SignupPage login={login} themeStyles={themeStyles} />
            } />
            <Route path="/chat" element={
              <ChatPage
                isAuthenticated={isAuthenticated}
                user={user}
                anonymousUsage={anonymousUsage}
                setAnonymousUsage={setAnonymousUsage}
                themeStyles={themeStyles}
              />
            } />
            <Route path="/generate" element={
              <GeneratePage
                isAuthenticated={isAuthenticated}
                user={user}
                anonymousUsage={anonymousUsage}
                setAnonymousUsage={setAnonymousUsage}
                themeStyles={themeStyles}
              />
            } />
            <Route path="/history" element={
  isAuthenticated ? <ChatHistoryPage /> : <Navigate to="/login" />
} />
            <Route path="/profile" element={
              isAuthenticated ? 
                <ProfilePage user={user} logout={logout} themeStyles={themeStyles} /> : 
                <Navigate to="/login" />
            } />
            <Route path="/images" element={
              isAuthenticated ? 
                <GeneratedImagesPage themeStyles={themeStyles} /> : 
                <Navigate to="/login" />
            } />
            <Route path="/videos" element={
              isAuthenticated ? 
                <GeneratedVideosPage themeStyles={themeStyles} /> : 
                <Navigate to="/login" />
            } />
            <Route path="/shared/chat/:shareId" element={<SharedChatView themeStyles={themeStyles} />} />
            <Route path="/pricing" element={<PricingPage themeStyles={themeStyles} />} />
            <Route path="/privacy" element={<PrivacyPage themeStyles={themeStyles} />} />
            <Route path="/contact" element={<ContactPage themeStyles={themeStyles} />} />
            <Route path="/verify-email" element={<EmailVerificationPage themeStyles={themeStyles} />} />
            <Route path="/admin/dashboard" element={<AdminDashboard themeStyles={themeStyles} />} />
            <Route path="/success" element={<PaymentSuccessPage />} />
            // Add this route in your App.js Routes component
<Route path="/payment-callback" element={<PaymentCallbackPage />} />
          </Routes>
          
           </ThemeTransition>

          {/* Updated Footer with theme support */}
          <footer className={`mt-8 py-6 border-t ${themeStyles.border}`}>
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${themeStyles.secondaryText}`}>
                  © {new Date().getFullYear()} AI Genesis
                </span>
                <span className="text-xs text-purple-400 font-bold">T.O.P.</span>
              </div>
            </div>
            
            <div className="flex space-x-6 mb-4 md:mb-0">
              <Link to="/privacy" className={`text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}>
                Privacy & Terms
              </Link>
              <Link to="/contact" className={`text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}>
                Contact
              </Link>
              <a 
                href="mailto:info@aigenesis.top" 
                className={`text-sm ${themeStyles.secondaryText} hover:${themeStyles.text} transition-colors`}
              >
                Support
              </a>
            </div>
            
            {/* Animated theme toggle in footer */}
            <ThemeToggle 
              theme={theme} 
              toggleTheme={toggleTheme} 
              themeStyles={themeStyles} 
            />
          </div>
        </div>
      </footer>
    </div>
  </Router>
</ThemeContext.Provider>
  );
}

export default App;